/**
 * Sidebar - Hive-OS Style Sidebar
 * Matches the 'La Ruche' prototype design.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from '@/hooks/useTranslation';
import { agentService } from '@/lib/agents.service';
import type { Agent } from '@/lib/agents.config';
import { mainNavigation } from '@/lib/navigation.config';
import * as Icons from '../icons/Icons';
import Avatar from '../ui/Avatar';

interface SidebarProps {
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  selectedAgentId?: string | null;
  onSelectAgent?: (agentId: string) => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  isCollapsed,
  onToggleCollapse,
  selectedAgentId,
  onSelectAgent,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { t } = useTranslation();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const profileDropdownRef = useRef<HTMLDivElement>(null);

  // Close profile dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSignOut = async () => {
    try {
      await signOut();
      setIsProfileOpen(false);
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  // Load agents
  useEffect(() => {
    let mounted = true;
    agentService.getAllAgents()
      .then(list => {
        if (mounted) {
          setAgents(list);
          setLoading(false);
        }
      })
      .catch(err => {
        console.error('Error loading agents:', err);
        if (mounted) setLoading(false);
      });
    return () => { mounted = false; };
  }, []);

  const handleAgentClick = (agent: Agent) => {
    onSelectAgent?.(agent.id);
    navigate(`/agents/${agent.slug}/dashboard`);
  };

  return (
    <aside
      className={`${
        isCollapsed ? 'w-20' : 'w-64'
      } h-screen hidden sm:flex flex-col border-r border-black/5 dark:border-white/5 bg-background-panel transition-all duration-700 ease-[cubic-bezier(0.4,0,0.2,1)] shrink-0 overflow-hidden relative z-40`}
    >
      <div className="p-3 lg:p-4 flex flex-col h-full">
        {/* Logo Section */}
        <div
          className={`flex items-center gap-3 mb-8 group cursor-pointer ${
            isCollapsed ? 'justify-center' : ''
          }`}
          onClick={() => navigate('/home')}
        >
          <div className="w-10 h-10 shrink-0 bg-amber-500 rounded-xl flex items-center justify-center shadow-[0_0_15px_rgba(245,158,11,0.3)] group-hover:scale-105 transition-transform">
            <span className="text-xl">🐝</span>
          </div>
          {!isCollapsed && (
            <h1 className="text-xl font-display font-bold bg-gradient-to-r from-amber-500 to-amber-700 bg-clip-text text-transparent truncate animate-in fade-in slide-in-from-left-2">
              SmartBeez
            </h1>
          )}
        </div>

        {/* Global Navigation */}
        <nav className="space-y-1 mb-8">
          {mainNavigation.map((item) => {
            const isActive = location.pathname.startsWith(item.path);
            const IconComponent = Icons[item.icon] || Icons.Home;

            return (
              <Link
                key={item.id}
                to={item.path}
                title={isCollapsed ? t(item.label) : undefined}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all group ${
                  isActive
                    ? 'bg-blue-600/10 dark:bg-blue-500/20 font-bold shadow-sm'
                    : 'hover:bg-black/5 dark:hover:bg-white/5'
                } ${isCollapsed ? 'justify-center' : ''}`}
              >
                <IconComponent className={`w-5 h-5 shrink-0 ${isActive ? 'text-blue-600 dark:text-blue-400' : 'text-slate-700 dark:text-slate-300 group-hover:text-slate-900 dark:group-hover:text-white'}`} />
                {!isCollapsed && (
                  <span className={`text-sm truncate animate-in fade-in slide-in-from-left-2 ${isActive ? 'text-blue-600 dark:text-blue-400' : 'text-slate-700 dark:text-slate-300 group-hover:text-slate-900 dark:group-hover:text-white'}`}>
                    {t(item.label)}
                  </span>
                )}
                {!isCollapsed && item.badge && (
                  <span className="ml-auto text-[10px] font-black bg-blue-600 text-white px-1.5 py-0.5 rounded-full">
                    {item.badge}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Agents Section */}
        <div className="space-y-3 flex-1 overflow-hidden">
          <div
            className={`flex items-center justify-between px-3 ${
              isCollapsed ? 'justify-center' : ''
            }`}
          >
            {!isCollapsed ? (
              <>
                <h2 className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-[0.2em] truncate">
                  {t('navigation.agents')}
                </h2>
                <button
                  onClick={() => navigate('/home')}
                  className="text-slate-500 dark:text-white hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
                >
                  <Icons.PlusIcon className="w-3.5 h-3.5" />
                </button>
              </>
            ) : (
              <div className="h-px w-6 bg-black/10 dark:bg-white/10" />
            )}
          </div>

          <div className="space-y-1.5 max-h-full overflow-y-auto pr-1 custom-scrollbar">
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className={`flex items-center gap-3 px-2 py-2.5 rounded-xl animate-pulse ${isCollapsed ? 'justify-center' : ''}`}>
                  <div className="w-8 h-8 rounded-full bg-black/10 dark:bg-white/10" />
                  {!isCollapsed && <div className="h-3 bg-black/10 dark:bg-white/10 rounded w-20" />}
                </div>
              ))
            ) : (
              agents.map((agent) => {
                const isActive = selectedAgentId === agent.id || location.pathname.includes(`/agents/${agent.slug}`);
                const translatedName = agent.customName || t(agent.name);
                const firstName = translatedName.split(' ')[0];

                return (
                  <button
                    key={agent.id}
                    onClick={() => handleAgentClick(agent)}
                    title={isCollapsed ? translatedName : undefined}
                    className={`w-full flex flex-col items-center gap-1 px-1 py-2.5 rounded-xl border transition-all group ${
                      isActive
                        ? 'bg-black/5 dark:bg-white/5 border-black/10 dark:border-white/10 font-bold'
                        : 'border-transparent text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-black/[0.02] dark:hover:bg-white/[0.02]'
                    } ${isCollapsed ? 'justify-center' : 'items-start px-2'}`}
                  >
                    <div className={`relative shrink-0 flex items-center ${isCollapsed ? 'justify-center w-full mb-0.5' : 'gap-3 w-full'}`}>
                      <Avatar
                        src={agent.avatar}
                        alt={agent.customName || agent.name}
                        size={isCollapsed ? 32 : 32}
                        className={`transition-all ${isActive ? 'ring-2 ring-blue-500 ring-offset-2 ring-offset-background-panel' : ''}`}
                      />
                      {!isCollapsed && (
                        <div className="flex-1 text-left min-w-0">
                          <p className="text-xs font-bold truncate leading-tight text-slate-900 dark:text-white">
                            {agent.customName || t(agent.name)}
                          </p>
                          <p className="text-[10px] text-slate-500 dark:text-slate-400 truncate">
                            {t(agent.name)}
                          </p>
                        </div>
                      )}
                      {isActive && !isCollapsed && (
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.6)]" />
                      )}
                    </div>
                    
                    {isCollapsed && (
                      <span className="text-[9px] font-bold text-slate-700 dark:text-white text-center w-full truncate px-0.5 uppercase tracking-tighter">
                        {firstName}
                      </span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Toggle & Profile */}
        <div className="mt-auto pt-4 space-y-3">
          <button
            onClick={onToggleCollapse}
            className="w-full flex items-center justify-center p-2 rounded-lg bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10 text-slate-500 dark:text-white hover:text-slate-700 dark:hover:text-slate-200 transition-all border border-black/5 dark:border-white/5"
          >
            <Icons.ChevronLeft className={`w-4 h-4 transition-transform duration-300 ${isCollapsed ? 'rotate-180' : ''}`} />
          </button>

          <div className="relative" ref={profileDropdownRef}>
            <button
              onClick={() => setIsProfileOpen(!isProfileOpen)}
              className={`group p-1.5 rounded-xl bg-black/5 dark:bg-black/20 border border-black/5 dark:border-white/5 hover:bg-black/10 dark:hover:bg-white/5 transition-all w-full ${isCollapsed ? 'flex justify-center' : 'flex items-center gap-3'}`}
              aria-expanded={isProfileOpen}
            >
              <div className="w-9 h-9 shrink-0 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/25 group-hover:scale-105 transition-transform">
                <span className="text-xs font-bold text-white leading-none">
                  {user?.full_name?.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase() || 'U'}
                </span>
              </div>
              {!isCollapsed && (
                <div className="flex-1 text-left animate-in fade-in slide-in-from-left-2 overflow-hidden">
                  <p className="text-xs font-bold truncate leading-tight text-slate-900 dark:text-white">{user?.full_name || 'User'}</p>
                  <p className="text-[9px] text-slate-500 dark:text-slate-400 truncate">{user?.email || 'View profile'}</p>
                </div>
              )}
            </button>

            {isProfileOpen && (
              <div className={`absolute ${isCollapsed ? 'left-full ml-2' : 'left-0'} bottom-full mb-2 w-48 rounded-xl bg-white dark:bg-slate-900 border border-black/10 dark:border-white/10 shadow-xl overflow-hidden z-50 animate-in fade-in zoom-in-95 duration-200`}>
                <div className="p-1.5">
                  <button
                    onClick={() => {
                      navigate('/profile');
                      setIsProfileOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 text-xs font-bold text-slate-700 dark:text-white hover:bg-black/5 dark:hover:bg-white/5 rounded-lg transition-all"
                  >
                    <Icons.UserIcon className="w-4 h-4" />
                    {t('auth.profile.viewProfile')}
                  </button>
                  <button
                    onClick={handleSignOut}
                    className="w-full flex items-center gap-3 px-3 py-2.5 text-xs font-bold text-rose-500 hover:bg-rose-500/10 rounded-lg transition-all"
                  >
                    <Icons.LogOut className="w-4 h-4" />
                    {t('auth.signOut')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;